<script setup>
// §8.5 PeriodSelector — financial-year aware (Reporting spec). Segmented
// preset control + explicit range. The FY presets respect the firm's FY start
// (AU/UK commonly 1 July / 6 April), so "This financial year" is not a fixed
// calendar assumption. Prev/next step the active grain. Numerals tabular (§5).
import { computed } from 'vue'
import LIconButton from '../actions/IconButton.vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  presets: {
    type: Array,
    default: () => [
      { value: 'period', label: 'This period' },
      { value: 'month', label: 'Month' },
      { value: 'quarter', label: 'Quarter' },
      { value: 'fy', label: 'Financial year' },
    ],
  },
  rangeLabel: { type: String, default: '1–14 July 2026' },
  // A plain range with prev/next and no preset segments (a week picker, say). `v-model` is then unused.
  showPresets: { type: Boolean, default: true },
  // Stop prev/next at the edge of the range that can be shown (e.g. the Approvals 5-week window).
  prevDisabled: { type: Boolean, default: false },
  nextDisabled: { type: Boolean, default: false },
  prevLabel: { type: String, default: 'Previous period' },
  nextLabel: { type: String, default: 'Next period' },
  // Make the range label a drop-down of the ranges that can be picked ([{ value, label }]), so a far-away one is one
  // click, not many prev/next steps. `rangeValue` is the option currently shown; picking another emits `select` with its value.
  rangeOptions: { type: Array, default: null },
  rangeValue: { type: String, default: '' },
  rangeSelectLabel: { type: String, default: 'Choose a period' },
})
const model = defineModel({ default: 'period' })
defineEmits(['prev', 'next', 'select'])

const activeLabel = computed(() => props.presets.find((p) => p.value === model.value)?.label)
</script>

<template>
  <div class="l-period">
    <div v-if="showPresets" class="seg" role="tablist" :aria-label="'Period preset'">
      <button
        v-for="p in presets"
        :key="p.value"
        role="tab"
        type="button"
        class="seg-btn"
        :class="{ on: model === p.value }"
        :aria-selected="model === p.value"
        @click="model = p.value"
      >
        {{ p.label }}
      </button>
    </div>
    <div class="range">
      <LIconButton :label="prevLabel" :disabled="prevDisabled" class="no-tooltip-icon" @click="$emit('prev')">
        <LIcon name="chevron-left" :size="16" />
      </LIconButton>
      <span v-if="!rangeOptions" class="range-label num">{{ rangeLabel }}</span>
      <span v-else class="range-pick">
        <select class="range-select num" :value="rangeValue" :aria-label="rangeSelectLabel" @change="$emit('select', $event.target.value)">
          <option v-for="o in rangeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <LIcon name="chevron-down" :size="14" class="range-chev" />
      </span>
      <LIconButton :label="nextLabel" :disabled="nextDisabled" class="no-tooltip-icon" @click="$emit('next')">
        <LIcon name="chevron-right" :size="16" />
      </LIconButton>
    </div>
  </div>
</template>

<style scoped>
.l-period {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  flex-wrap: wrap;
}
.seg {
  display: inline-flex;
  padding: 3px;
  background: var(--surface-sunken);
  border: 1px solid var(--line);
  border-radius: var(--r);
}
.seg-btn {
  font-family: var(--font-ui);
  font-size: var(--text-chip);
  font-weight: 600;
  color: var(--text-2);
  padding: 6px 12px;
  border: 0;
  background: none;
  border-radius: var(--r-sm);
  cursor: pointer;
  min-height: var(--target-min);
}
.seg-btn:hover {
  color: var(--text);
}
.seg-btn.on {
  background: var(--surface);
  color: var(--text);
  box-shadow: var(--shadow-sm);
}
.range {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
}
.range-label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text);
  min-width: 130px;
  text-align: center;
}
.range-pick {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.range-select {
  appearance: none;
  -webkit-appearance: none;
  font-family: var(--font-ui);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text);
  min-width: 190px;
  padding: 6px 30px 6px 12px;
  border: 1px solid var(--line);
  border-radius: var(--r);
  background: var(--surface);
  min-height: var(--target-min);
  cursor: pointer;
}
.range-select:hover {
  border-color: var(--line-strong);
}
.range-select:focus-visible {
  border-color: var(--interactive);
}
.range-chev {
  position: absolute;
  right: 10px;
  color: var(--icon);
  pointer-events: none;
}
</style>
